import type { Plugin } from "../../../core/plugin";
import { PluginRegistry } from "../../../core/pluginRegistry";

/**
 * ltnTimeline — raíl de "Proceso de incorporación" para la home de LTN
 * (LTNW-1).
 *
 * Resalta la fase sobre la que está el ratón y marca todas las anteriores
 * como alcanzadas, así el raíl se lee como progreso en vez de como seis
 * tarjetas iguales.
 *
 * El prototipo solo respondía a `mouseenter`, lo que dejaba la interacción
 * inalcanzable por teclado; aquí cada fase es un <button> real y el foco
 * controla el mismo estado.
 *
 * Contrato de marcado:
 *   <div data-plugin="ltnTimeline">
 *     <button data-ltn-phase>…</button>   ← repetido
 */
export class LtnTimeline implements Plugin {
  private phases: HTMLElement[] = [];
  private active = 0;

  private readonly onPointer = (event: Event) => {
    const phase = (event.target as HTMLElement).closest<HTMLElement>(
      "[data-ltn-phase]",
    );
    if (phase) this.setActive(this.phases.indexOf(phase));
  };

  private readonly onFocus = (event: FocusEvent) => {
    const phase = (event.target as HTMLElement).closest<HTMLElement>(
      "[data-ltn-phase]",
    );
    if (phase) this.setActive(this.phases.indexOf(phase));
  };

  constructor(private readonly element: HTMLElement) {}

  init(): void {
    this.phases = [
      ...this.element.querySelectorAll<HTMLElement>("[data-ltn-phase]"),
    ];
    if (!this.phases.length) return;

    this.element.addEventListener("mouseover", this.onPointer);
    this.element.addEventListener("focusin", this.onFocus);
    this.paint();
  }

  destroy(): void {
    this.element.removeEventListener("mouseover", this.onPointer);
    this.element.removeEventListener("focusin", this.onFocus);
  }

  private setActive(index: number): void {
    if (index < 0 || index === this.active) return;
    this.active = index;
    this.paint();
  }

  private paint(): void {
    this.phases.forEach((phase, i) => {
      phase.classList.toggle("is-active", i === this.active);
      phase.classList.toggle("is-reached", i <= this.active);
    });
  }
}

PluginRegistry.register("ltnTimeline", LtnTimeline);
